import { useContext, useCallback, useEffect, useState, useRef, useMemo, RefObject } from "react"
import classNames from "classnames"
import { Typography, Box, IconButton, Button } from "@mui/material"
import { Close, TuneOutlined } from "@mui/icons-material"
import { AnimatedLoadingButton, Setup, Rules } from "./components"
import { useOptimization } from "hooks/optimizer"
import useDebouncedCallback from "hooks/debounced-callback"
import { LOADING, ERROR } from "helpers/data-statuses"
import { IsoDate } from "helpers/types/dates"
import {
  createConfigurationSections,
  DEFAULT_CONFIGURATION,
  SetupSection,
  RulesSection,
  DefaultConfiguration,
  ConfigurationSection,
  SectionName,
} from "./configuration-sections"
import { transformConfigurationToForm, transformFormToConfiguration } from "./components/helpers/configuration"
import { OptimizeExperimentsProps } from "./types"
import { keys } from "helpers/types/collections"
import { useIsDashboardOpLayoutEnabled } from "resources/account"

import "./styles.scss"

export default function OptimizeExperiments({ context, ...props }: OptimizeExperimentsProps) {
  const isDashboardOpLayoutEnabled = useIsDashboardOpLayoutEnabled()
  const {
    setIsOptimizedExperimentsDrawerOpen,
    isOptimizedExperimentsDrawerOpen,
    endDate,
    modelId,
    base,
    setComparisonInstrument,
    setOptimizeConfiguration,
    setPreviewOptimizeConfiguration,
    previewOptimizeConfiguration,
    optimizeConfiguration,
  } = useContext(context)
  const { instrumentsWithMetadata, modelMetadata } = props
  const { original } = instrumentsWithMetadata || {}

  const getInitialConfiguration = (): { setup: SetupSection, rules: RulesSection } => {
    const configuration = previewOptimizeConfiguration || optimizeConfiguration
    if (configuration) {
      return transformConfigurationToForm(configuration)
    }
    return {
      setup: DEFAULT_CONFIGURATION.setup,
      rules: DEFAULT_CONFIGURATION.rules,
    }
  }

  const initialConfiguration = getInitialConfiguration()
  const [setup, setSetup] = useState<SetupSection>(initialConfiguration.setup)
  const [rules, setRules] = useState<RulesSection>(initialConfiguration.rules)

  // Manage the form closure to make sure we get the latest
  // values from each section before running the optimization
  const initialSetupValues = useRef<SetupSection>(initialConfiguration.setup)
  const initialRulesValues = useRef<RulesSection>(initialConfiguration.rules)
  const initialEndDate = useRef<IsoDate>(endDate)
  const latestSetup = useRef<SetupSection>(setup)
  const latestRules = useRef<RulesSection>(rules)
  const latestEndDate = useRef<IsoDate>(endDate)

  const { runOptimization, optimizedPositionSet, optimizationStatus } = useOptimization(original, modelId, endDate, base, { setup, rules })

  // Keep track of the latest runOptimization function that
  // has the latest values from the various form sections
  const latestRunOptimization = useRef<() => void>(runOptimization)

  useEffect(() => {
    latestSetup.current = setup
    latestRules.current = rules
    latestEndDate.current = endDate
    latestRunOptimization.current = runOptimization
  }, [setup, rules, endDate, runOptimization])

  const isOptimizing = optimizationStatus === LOADING
  const hasError = optimizationStatus === ERROR
  const canOptimize = Boolean(
    modelMetadata?.id
    && instrumentsWithMetadata?.original?.id
    && !isOptimizing
    && original
    && modelId
    && endDate,
  )

  const isSaveRef = useRef<boolean>(false)

  useEffect(() => {
    if (optimizedPositionSet) {
      setComparisonInstrument(optimizedPositionSet)
      if (isSaveRef.current) {
        setIsOptimizedExperimentsDrawerOpen(false)
        isSaveRef.current = false
      }
    }
  }, [optimizedPositionSet, setComparisonInstrument, setIsOptimizedExperimentsDrawerOpen])

  useEffect(() => {
    if (hasError) {
      setIsOptimizedExperimentsDrawerOpen(false)
    }
  }, [hasError, setIsOptimizedExperimentsDrawerOpen])

  // Only run and save preview configuration when the panel is open (live preview/edit mode)
  // If there's a change, wait 3 seconds to see if there's another change
  // If there's no change, run the optimization
  const debouncedRunOptimization = useDebouncedCallback(() => {
    if (canOptimize && isOptimizedExperimentsDrawerOpen) {
      latestRunOptimization.current()

      const currentConfiguration = transformFormToConfiguration(latestEndDate.current, latestSetup.current, latestRules.current)
      setPreviewOptimizeConfiguration(currentConfiguration)
    }
  }, 3000)

  const handleSaveAndOptimize = useCallback(() => {
    if (canOptimize) {
      isSaveRef.current = true
      runOptimization()

      const currentConfiguration = transformFormToConfiguration(endDate, setup, rules)
      setOptimizeConfiguration(currentConfiguration)

      setPreviewOptimizeConfiguration(null)
    }
  }, [runOptimization, canOptimize, setOptimizeConfiguration, setPreviewOptimizeConfiguration, setup, rules, endDate])

  const handleClosePanel = useCallback(() => {
    setIsOptimizedExperimentsDrawerOpen(false)
  }, [setIsOptimizedExperimentsDrawerOpen])

  const configurationSections = useMemo(() => createConfigurationSections({
    setup,
    setSetup,
    latestSetup,
    initialSetupValues,
    rules,
    setRules,
    latestRules,
    initialRulesValues,
  }), [setup, rules])

  const handleFormChange = useCallback(<T extends ConfigurationSection>(
    newState: T,
    setState: (state: T) => void,
    latestStateRef: RefObject<T>,
    initialValuesRef: RefObject<T>,
  ) => {
    setState(newState)
    latestStateRef.current = newState

    const hasSectionChanged = (keys(newState)).some(key => {
      const currentValue = newState[key]
      const initialValue = initialValuesRef.current?.[key]
      return currentValue !== initialValue
    })
    const hasDateChanged = initialEndDate.current !== endDate
    const hasChanged = hasSectionChanged || hasDateChanged

    if (hasChanged) {
      debouncedRunOptimization()
    }
  }, [endDate, debouncedRunOptimization])

  const createChangeHandler = useCallback(<T extends SectionName>(sectionName: T) => {
    return (newState: DefaultConfiguration[T]) => {
      const section = configurationSections[sectionName]
      if (section) {
        handleFormChange(newState, section.setState, section.latestRef, section.initialRef)
      }
    }
  }, [configurationSections, handleFormChange])

  const handleSetupChange = useMemo(() => createChangeHandler("setup"), [createChangeHandler])
  const handleRulesChange = useMemo(() => createChangeHandler("rules"), [createChangeHandler])
  const handleDateChange = useCallback(() => {
    handleFormChange(setup, setSetup, latestSetup, initialSetupValues)
  }, [handleFormChange, setup, setSetup, latestSetup, initialSetupValues])

  useEffect(() => {
    handleDateChange()
  }, [endDate, handleDateChange])

  return (
    <div className={classNames("optimize-experiments-content", { "use-dashboard-layout": isDashboardOpLayoutEnabled })}>
      <Box className="content">
        <Box className="header">
          <TuneOutlined color="primary" />
          <Typography variant="h6">
            <span>Optimized Experiments</span>
          </Typography>
          <Box className="header-actions">
            <IconButton onClick={handleClosePanel} size="medium" color="inherit">
              <Close />
            </IconButton>
          </Box>
        </Box>
        <div className="scrollable-content">
          <Box className="body">
            <Box className="body-section">
              <Setup
                setup={setup}
                onSetupChange={handleSetupChange}
              />
            </Box>
            <Box className="body-section">
              <Rules
                rules={rules}
                onRulesChange={handleRulesChange}
              />
            </Box>
            <Box className="actions">
              {isOptimizing ? (
                <AnimatedLoadingButton text="Optimizing..." />
              ) : (
                <Button
                  variant="contained"
                  color={hasError ? "error" : "primary"}
                  onClick={handleSaveAndOptimize}
                  disabled={!canOptimize}
                >
                  {hasError ? "Optimization Failed - Retry" : "Run and Save"}
                </Button>
              )}
            </Box>
          </Box>
        </div>
      </Box>
    </div>
  )
}
